import React, { useState } from 'react';
import { Upload, Download, FileSpreadsheet, Users, PawPrint, Calendar, FileText, Stethoscope, AlertCircle, CheckCircle, Loader2, HelpCircle, ChevronDown, ChevronUp, ArrowRight, Monitor, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IntegrationManager = () => {
  const [importing, setImporting] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [showGuide, setShowGuide] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const token = localStorage.getItem('vetToken');

  // Liste des logiciels compatibles
  const compatibleSoftware = [
    { name: 'Vétocom', share: '~40%', popular: true },
    { name: 'Bourgelat', share: 'Important', popular: true },
    { name: 'Assistovet', share: 'Important', popular: true },
    { name: 'DrVeto', share: 'Croissant', popular: false },
    { name: 'Vet\'Phi', share: 'Significatif', popular: false },
    { name: 'Vetup', share: 'Moderne', popular: false },
    { name: 'GMVet', share: 'Centravet', popular: false },
  ];

  const faqItems = [
    {
      question: "Pourquoi utiliser l'import/export CSV ?",
      answer: "Le format CSV est universel et compatible avec 100% des logiciels vétérinaires. C'est le moyen le plus simple de transférer vos données entre vetoRDV et votre logiciel de gestion (Vétocom, Bourgelat, etc.) sans risque de perte de données."
    },
    {
      question: "Comment exporter mes données depuis mon logiciel actuel ?",
      answer: "Dans votre logiciel vétérinaire, cherchez l'option 'Export' ou 'Extraction de données'. Sélectionnez le format CSV ou Excel. Si vous avez besoin d'aide, consultez la documentation de votre logiciel ou contactez leur support."
    },
    {
      question: "Que faire si mon fichier CSV ne fonctionne pas ?",
      answer: "Vérifiez que : 1) Le fichier utilise le point-virgule (;) comme séparateur, 2) La première ligne contient les en-têtes de colonnes, 3) Les dates sont au format JJ/MM/AAAA ou AAAA-MM-JJ. Téléchargez notre template comme référence."
    },
    {
      question: "Mes données sont-elles sécurisées ?",
      answer: "Oui, absolument. Les données importées sont chiffrées et stockées de manière sécurisée. Seul vous et les propriétaires d'animaux concernés peuvent y accéder. Nous respectons le RGPD."
    },
    {
      question: "Puis-je importer l'historique médical complet ?",
      answer: "Oui ! Vous pouvez importer les vaccinations, traitements, consultations, chirurgies et allergies. Utilisez le template 'Historique médical' pour voir le format attendu."
    }
  ];

  const downloadTemplate = async (type) => {
    try {
      const response = await fetch(`${API_URL}/api/integration/templates/${type}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `template_import_${type}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Template téléchargé');
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleImport = async (type, file) => {
    if (!file) return;
    
    setImporting(type);
    setImportResults(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/integration/import/${type}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setImportResults(data);
        if (data.imported > 0) {
          toast.success(`${data.imported} éléments importés avec succès !`);
        }
        if (data.skipped > 0) {
          toast.info(`${data.skipped} éléments ignorés (déjà existants)`);
        }
      } else {
        toast.error(data.detail || 'Erreur lors de l\'import');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'import');
    } finally {
      setImporting(null);
    }
  };

  const handleExport = async (type, params = {}) => {
    try {
      toast.loading('Export en cours...');
      
      let url = `${API_URL}/api/integration/export/${type}`;
      if (Object.keys(params).length > 0) {
        url += '?' + new URLSearchParams(params).toString();
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        
        const contentDisposition = response.headers.get('content-disposition');
        const filename = contentDisposition 
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `export_${type}_${new Date().toISOString().slice(0,10)}.csv`;
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        
        toast.dismiss();
        toast.success('Export téléchargé avec succès !');
      } else {
        toast.dismiss();
        toast.error('Erreur lors de l\'export');
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Erreur lors de l\'export');
    }
  };

  const ImportCard = ({ type, icon: Icon, title, description }) => (
    <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow hover:border-blue-300">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-blue-50 rounded-lg">
          <Icon className="w-6 h-6 text-blue-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
          <p className="text-sm text-gray-500 mb-4">{description}</p>
          
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate(type)}
              className="text-gray-600 hover:text-blue-600 hover:border-blue-300"
            >
              <Download className="w-4 h-4 mr-1" />
              Télécharger le modèle
            </Button>
            
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => handleImport(type, e.target.files[0])}
                disabled={importing === type}
              />
              <Button
                variant="default"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={importing === type}
                asChild
              >
                <span>
                  {importing === type ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-1" />
                  )}
                  Importer
                </span>
              </Button>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const ExportCard = ({ type, icon: Icon, title, description, params }) => (
    <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow hover:border-green-300">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-green-50 rounded-lg">
          <Icon className="w-6 h-6 text-green-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
          <p className="text-sm text-gray-500 mb-4">{description}</p>
          
          <Button
            variant="default"
            size="sm"
            onClick={() => handleExport(type, params)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Download className="w-4 h-4 mr-1" />
            Exporter
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header avec explication */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 rounded-2xl p-8 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <RefreshCw className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Synchronisez vos données</h2>
            <p className="text-indigo-200">Intégration avec votre logiciel vétérinaire</p>
          </div>
        </div>
        
        <p className="text-lg text-indigo-100 mb-6 leading-relaxed">
          Transférez facilement vos clients, animaux et historiques médicaux entre <strong>vetoRDV</strong> et 
          votre logiciel de gestion vétérinaire grâce à notre système d'import/export CSV universel.
        </p>

        <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
          <p className="text-sm text-indigo-100 flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
            <span>
              <strong className="text-white">Compatible avec tous les logiciels</strong> : Vétocom, Bourgelat, 
              Assistovet, DrVeto, Vet'Phi, Vetup, GMVet et tout autre logiciel supportant l'export CSV.
            </span>
          </p>
        </div>
      </div>

      {/* Guide étape par étape */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl overflow-hidden">
        <button 
          onClick={() => setShowGuide(!showGuide)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-amber-100/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <HelpCircle className="w-5 h-5 text-amber-600" />
            </div>
            <span className="font-semibold text-gray-900">📖 Guide : Comment synchroniser vos données ?</span>
          </div>
          {showGuide ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
        </button>
        
        {showGuide && (
          <div className="px-6 pb-6 space-y-6">
            {/* Étapes */}
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              {/* Étape 1 */}
              <div className="bg-white rounded-xl p-5 border border-amber-100 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</span>
                  <h4 className="font-semibold text-gray-900">Téléchargez le modèle</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Cliquez sur <strong>"Télécharger le modèle"</strong> pour obtenir un fichier CSV avec 
                  la structure exacte attendue et des exemples.
                </p>
                <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded-lg">
                  💡 Le modèle contient des exemples pour vous guider
                </div>
              </div>

              {/* Étape 2 */}
              <div className="bg-white rounded-xl p-5 border border-amber-100 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</span>
                  <h4 className="font-semibold text-gray-900">Exportez vos données</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Dans votre logiciel actuel (Vétocom, Bourgelat...), exportez vos données au format CSV 
                  puis copiez-les dans notre modèle.
                </p>
                <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded-lg">
                  💡 Gardez la première ligne (en-têtes) du modèle
                </div>
              </div>

              {/* Étape 3 */}
              <div className="bg-white rounded-xl p-5 border border-amber-100 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</span>
                  <h4 className="font-semibold text-gray-900">Importez sur vetoRDV</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Cliquez sur <strong>"Importer"</strong> et sélectionnez votre fichier. 
                  Les données seront ajoutées automatiquement.
                </p>
                <div className="text-xs text-green-700 bg-green-50 p-2 rounded-lg">
                  ✓ Les doublons sont automatiquement détectés
                </div>
              </div>
            </div>

            {/* Logiciels compatibles */}
            <div className="bg-white rounded-xl p-5 border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Monitor className="w-5 h-5 text-indigo-600" />
                Logiciels compatibles
              </h4>
              <div className="flex flex-wrap gap-2">
                {compatibleSoftware.map((software, i) => (
                  <span 
                    key={i} 
                    className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                      software.popular 
                        ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' 
                        : 'bg-gray-100 text-gray-700 border border-gray-200'
                    }`}
                  >
                    {software.name}
                    {software.popular && <span className="ml-1">⭐</span>}
                  </span>
                ))}
                <span className="px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm border border-green-200">
                  + Tout logiciel avec export CSV
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Import Results */}
      {importResults && (
        <div className={`p-5 rounded-xl border-2 ${
          importResults.errors?.length > 0 
            ? 'bg-yellow-50 border-yellow-300' 
            : 'bg-green-50 border-green-300'
        }`}>
          <div className="flex items-start gap-3">
            {importResults.errors?.length > 0 ? (
              <AlertCircle className="w-6 h-6 text-yellow-600 mt-0.5" />
            ) : (
              <CheckCircle className="w-6 h-6 text-green-600 mt-0.5" />
            )}
            <div>
              <p className="font-semibold text-gray-900 text-lg">
                Import terminé !
              </p>
              <p className="text-gray-700 mt-1">
                <span className="text-green-600 font-medium">{importResults.imported} éléments importés</span>
                {importResults.skipped > 0 && (
                  <span className="text-gray-500"> • {importResults.skipped} ignorés (déjà existants)</span>
                )}
              </p>
              {importResults.errors?.length > 0 && (
                <div className="mt-3 p-3 bg-white rounded-lg border border-yellow-200">
                  <p className="text-sm font-medium text-yellow-800 mb-2">Lignes avec erreurs :</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {importResults.errors.map((err, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-yellow-500">•</span>
                        {err}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Section Import */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Upload className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Importer des données</h3>
            <p className="text-sm text-gray-500">Transférez vos données depuis votre logiciel actuel vers vetoRDV</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ImportCard
            type="clients"
            icon={Users}
            title="Clients (propriétaires)"
            description="Importez vos clients avec leurs coordonnées : nom, email, téléphone, adresse..."
          />
          <ImportCard
            type="animaux"
            icon={PawPrint}
            title="Animaux"
            description="Importez les animaux : nom, espèce, race, date de naissance, puce électronique..."
          />
          <ImportCard
            type="historique"
            icon={Stethoscope}
            title="Historique médical"
            description="Importez les vaccinations, traitements, consultations, chirurgies, allergies..."
          />
        </div>
      </div>

      {/* Section Export */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <Download className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Exporter des données</h3>
            <p className="text-sm text-gray-500">Récupérez vos données vetoRDV pour les utiliser ailleurs</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <ExportCard
            type="clients"
            icon={Users}
            title="Clients"
            description="Liste complète de vos clients"
          />
          <ExportCard
            type="animaux"
            icon={PawPrint}
            title="Animaux"
            description="Tous les animaux de vos clients"
          />
          <ExportCard
            type="rdv"
            icon={Calendar}
            title="Rendez-vous"
            description="Historique complet des RDV"
          />
          <ExportCard
            type="factures"
            icon={FileText}
            title="Factures"
            description="Vos factures d'abonnement"
          />
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-indigo-600" />
          Questions fréquentes
        </h3>
        
        <div className="space-y-2">
          {faqItems.map((item, index) => (
            <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-900">{item.question}</span>
                {expandedFaq === index ? (
                  <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}
              </button>
              {expandedFaq === index && (
                <div className="px-5 pb-4 text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-3">
                  {item.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Support */}
      <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-indigo-100 rounded-lg">
            <FileSpreadsheet className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Besoin d'aide pour l'import ?</h4>
            <p className="text-sm text-gray-600 mb-3">
              Notre équipe peut vous accompagner gratuitement pour votre première synchronisation de données.
              Nous pouvons aussi vous aider à exporter vos données depuis votre logiciel actuel.
            </p>
            <a 
              href="/contact" 
              className="inline-flex items-center gap-1 text-indigo-600 font-medium text-sm hover:text-indigo-700"
            >
              Contactez-nous
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationManager;
